import { Avatar, Link, Stack, Typography } from '@mui/material';
import formatPhoneNumber from '../../utils/formatPhoneNumber';

export interface ShowingAgentFields {
  showingAgentName?: string | null;
  showingAgentPhone?: string | null;
  showingAgentEmail?: string | null;
  showingAgentCompany?: string | null;
  showingAgentAvatarUrl?: string | null;
}

interface Props {
  showing: ShowingAgentFields;
  /** Smaller avatar and caption-sized name, for a dense list row. */
  dense?: boolean;
  /** Offer the agent's email alongside the phone. Admin surfaces only. */
  showEmail?: boolean;
}

export const agentInitials = (name: string | null | undefined): string => {
  if (!name) return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
};

export const hasShowingAgent = (showing?: ShowingAgentFields | null): boolean =>
  !!showing?.showingAgentName;

export const AGENT_AWAITING_CONFIRMATION_LABEL = 'Agent assigned · Waiting on seller to confirm';

/**
 * A Showami agent has picked up the request, but the seller side has not yet
 * confirmed the time — the showing is still pending.
 */
export const isAgentAwaitingConfirmation = (
  showing: ShowingAgentFields & {
    accepted?: boolean | null;
    cancelledAt?: Date | string | null;
    showamiId?: string | null;
  }
): boolean =>
  showing.accepted === false &&
  !showing.cancelledAt &&
  !!showing.showamiId &&
  !!showing.showingAgentName;

/**
 * The agent who is showing — or who showed — the home.
 *
 * Populated from Showami when a showing is confirmed, and kept on the record
 * afterwards so the person who walked the property is still identifiable once
 * the showing is completed.
 */
const ShowingAgent: React.FC<Props> = ({ showing, dense = false, showEmail = false }) => {
  if (!showing.showingAgentName) return null;

  const size = dense ? 26 : 32;

  return (
    <Stack
      data-testid="showing-agent"
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ minWidth: 0 }}
    >
      <Avatar
        src={showing.showingAgentAvatarUrl || undefined}
        sx={{
          width: size,
          height: size,
          fontSize: dense ? 11 : 13,
          bgcolor: 'primary.light',
        }}
      >
        {agentInitials(showing.showingAgentName)}
      </Avatar>
      <Stack spacing={0} sx={{ minWidth: 0 }}>
        <Typography
          variant={dense ? 'caption' : 'body2'}
          fontWeight={600}
          noWrap
          sx={{ lineHeight: 1.4 }}
        >
          {showing.showingAgentName}
          {showing.showingAgentCompany ? ` — ${showing.showingAgentCompany}` : ''}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          sx={{ minWidth: 0, flexWrap: 'wrap' }}
        >
          {showing.showingAgentPhone && (
            <Link
              href={`tel:${showing.showingAgentPhone}`}
              variant="caption"
              color="text.secondary"
              underline="hover"
              onClick={(e) => e.stopPropagation()}
            >
              {formatPhoneNumber(showing.showingAgentPhone)}
            </Link>
          )}
          {showEmail && showing.showingAgentEmail && (
            <Link
              href={`mailto:${showing.showingAgentEmail}`}
              variant="caption"
              color="text.secondary"
              underline="hover"
              onClick={(e) => e.stopPropagation()}
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {showing.showingAgentEmail}
            </Link>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ShowingAgent;
